
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Settings, Save, Play, RotateCcw, BarChart2 } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";

// Define StewieQuantParams interface locally if needed
interface StewieQuantParams {
  correlationThreshold: number;
  zscoreThreshold: number;
  volatilityLookback: number;
  maxPositionSizePct: number;
  regimeChangeThreshold: number;
  reversionHalfLife: number;
  isVolWeightedRisk?: boolean;
  executionDelayMs: number;
  adaptivePositionSizing: boolean;
  regimeDetection: boolean;
  multiAssetClustering: boolean;
  aggregatedSymbols?: any[];
  backtest: {
    startDate: string;
    endDate: string;
    initialCapital: number;
  };
  [key: string]: any;
}

// Define the props interface with typed initialConfig
interface StewieQuantStrategyConfigProps {
  onSave: (config: StewieQuantParams) => void;
  onRun?: (config: StewieQuantParams) => void;
  initialConfig?: Partial<StewieQuantParams>;
}

const StewieQuantStrategyConfig: React.FC<StewieQuantStrategyConfigProps> = ({ 
  onSave, 
  onRun, 
  initialConfig = {} 
}) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<StewieQuantParams>({
    correlationThreshold: initialConfig.correlationThreshold || 0.7,
    zscoreThreshold: initialConfig.zscoreThreshold || 2.0,
    volatilityLookback: initialConfig.volatilityLookback || 20,
    maxPositionSizePct: initialConfig.maxPositionSizePct || 0.05,
    regimeChangeThreshold: initialConfig.regimeChangeThreshold || 0.3,
    reversionHalfLife: initialConfig.reversionHalfLife || 15,
    isVolWeightedRisk: initialConfig.isVolWeightedRisk !== false,
    executionDelayMs: initialConfig.executionDelayMs || 500,
    adaptivePositionSizing: initialConfig.adaptivePositionSizing !== false,
    regimeDetection: initialConfig.regimeDetection !== false,
    multiAssetClustering: initialConfig.multiAssetClustering !== false,
    aggregatedSymbols: initialConfig.aggregatedSymbols || [],
    backtest: {
      startDate: initialConfig.backtest?.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: initialConfig.backtest?.endDate || new Date().toISOString().split('T')[0],
      initialCapital: initialConfig.backtest?.initialCapital || 100000,
    }
  });

  const [isChanged, setIsChanged] = useState(false);

  const handleChange = (field, value) => {
    setConfig(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
    setIsChanged(true);
  };

  const handleSave = () => {
    onSave(config);
    toast({
      title: "Strategy Configuration Saved",
      description: "StewieQuant Algorithm parameters have been updated",
    });
    setIsChanged(false);
  };

  const handleReset = () => {
    setConfig({
      correlationThreshold: 0.7,
      zscoreThreshold: 2.0,
      volatilityLookback: 20,
      maxPositionSizePct: 0.05,
      regimeChangeThreshold: 0.3,
      reversionHalfLife: 15,
      isVolWeightedRisk: true,
      executionDelayMs: 500,
      adaptivePositionSizing: true,
      regimeDetection: true,
      multiAssetClustering: true,
      backtest: {
        startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        initialCapital: 100000,
      }
    });
    setIsChanged(true);
    toast({
      title: "Configuration Reset",
      description: "Parameters reset to default values",
    });
  };

  const formatPercentage = (value) => `${(value * 100).toFixed(0)}%`;
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LineChart className="w-5 h-5 text-blue-600" />
          <span>StewieQuant Algorithm Configuration</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-sm font-medium mb-4">Core Parameters</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="correlation-threshold" className="text-sm">
                Correlation Threshold
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="correlation-threshold"
                  min={0.3} 
                  max={0.9} 
                  step={0.05} 
                  value={[config.correlationThreshold]} 
                  onValueChange={([value]) => handleChange('correlationThreshold', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{config.correlationThreshold.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="zscore-threshold" className="text-sm">
                Z-Score Threshold
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="zscore-threshold"
                  min={1} 
                  max={3} 
                  step={0.1} 
                  value={[config.zscoreThreshold]} 
                  onValueChange={([value]) => handleChange('zscoreThreshold', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{config.zscoreThreshold.toFixed(1)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="volatility-lookback" className="text-sm">
                Volatility Lookback (days)
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="volatility-lookback"
                  min={5} 
                  max={50} 
                  step={1} 
                  value={[config.volatilityLookback]} 
                  onValueChange={([value]) => handleChange('volatilityLookback', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{config.volatilityLookback}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="position-size" className="text-sm">
                Max Position Size
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="position-size"
                  min={0.01} 
                  max={0.2} 
                  step={0.01} 
                  value={[config.maxPositionSizePct]} 
                  onValueChange={([value]) => handleChange('maxPositionSizePct', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formatPercentage(config.maxPositionSizePct)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4">Advanced Parameters</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="regime-threshold" className="text-sm">
                Regime Change Threshold
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="regime-threshold"
                  min={0.1} 
                  max={0.5} 
                  step={0.05} 
                  value={[config.regimeChangeThreshold]} 
                  onValueChange={([value]) => handleChange('regimeChangeThreshold', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{config.regimeChangeThreshold.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="half-life" className="text-sm">
                Reversion Half-Life (days)
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="half-life"
                  min={5} 
                  max={30} 
                  step={1} 
                  value={[config.reversionHalfLife]} 
                  onValueChange={([value]) => handleChange('reversionHalfLife', value)}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{config.reversionHalfLife}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="execution-delay" className="text-sm">
                Execution Delay (ms)
              </Label>
              <div className="flex items-center gap-2">
                <Slider 
                  id="execution-delay"
                  min={0} 
                  max={2000} 
                  step={100} 
                  value={[config.executionDelayMs]} 
                  onValueChange={([value]) => handleChange('executionDelayMs', value)}
                  className="flex-1"
                />
                <span className="w-16 text-sm text-right">{config.executionDelayMs}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4">Strategy Features</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="adaptive-sizing" className="text-sm cursor-pointer">
                Adaptive Position Sizing
              </Label>
              <Switch
                id="adaptive-sizing"
                checked={config.adaptivePositionSizing}
                onCheckedChange={(checked) => handleChange('adaptivePositionSizing', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="regime-detection" className="text-sm cursor-pointer">
                Market Regime Detection
              </Label>
              <Switch
                id="regime-detection"
                checked={config.regimeDetection}
                onCheckedChange={(checked) => handleChange('regimeDetection', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="multi-asset" className="text-sm cursor-pointer">
                Multi-Asset Clustering
              </Label>
              <Switch
                id="multi-asset"
                checked={config.multiAssetClustering}
                onCheckedChange={(checked) => handleChange('multiAssetClustering', checked)}
              />
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4">Backtest Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="start-date" className="text-sm">
                Start Date
              </Label>
              <Input
                id="start-date"
                type="date"
                value={config.backtest.startDate}
                onChange={(e) => handleChange('backtest.startDate', e.target.value)}
                className="h-9"
              />
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="end-date" className="text-sm">
                End Date
              </Label>
              <Input
                id="end-date"
                type="date"
                value={config.backtest.endDate}
                onChange={(e) => handleChange('backtest.endDate', e.target.value)}
                className="h-9"
              />
            </div>
            
            <div className="grid grid-cols-2 items-center gap-4">
              <Label htmlFor="initial-capital" className="text-sm">
                Initial Capital
              </Label>
              <Input
                id="initial-capital"
                type="number"
                min="10000"
                step="10000"
                value={config.backtest.initialCapital}
                onChange={(e) => handleChange('backtest.initialCapital', parseInt(e.target.value))}
                className="h-9"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <div>
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
          </div>
          <div>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={!isChanged}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Config
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StewieQuantStrategyConfig;
